import _ from 'lodash';
import { apiBaseUrl } from '../apiConsts';
import {
  buildPaginationQuery,
  createEmptyPagedResponse,
  getPagedMeta,
  MAX_PAGE_SIZE,
} from '../pagination';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import { isValidGroupResponse, isValidGroupsResponse } from '../apiValidators';

import type {
  Group,
  WithOptionalId,
  PagedResponse,
  PaginatedListParams,
} from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { getDefaultError } from '../../utils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

const mutateGroupAssociation = async (
  groupId: string,
  tableToRef: 'trainers' | 'members' | 'managers',
  action: 'associate' | 'unassociate',
  ids: string[],
): Promise<FetchResult<null>> => {
  if (!ids.length) {
    return createSuccessResult<null>(null);
  }
  return sendWithoutBody(
    `${apiBaseUrl}groups/${groupId}/${action}/${tableToRef}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(ids),
    },
  );
};

export const fetchGroupById = (
  groupId: string,
): Promise<FetchResult<Group | null>> => {
  if (!groupId) {
    return Promise.resolve({
      data: null,
      success: false,
      error: getDefaultError('Group not found'),
    });
  }
  return getDataWithRetry(
    `${apiBaseUrl}groups/${groupId}`,
    {},
    isValidGroupResponse,
  );
};

export const createNewGroup = async (
  group: WithOptionalId<Group>,
  trainerIds: string[],
  memberIds: string[],
): Promise<FetchResult<Group | null>> => {
  const safeTrainerIds = (trainerIds ?? []).filter(Boolean) as string[];
  const safeMemberIds = (memberIds ?? []).filter(Boolean) as string[];

  const { memberIds: _, managerIds: __, trainerIds: ___, ...groupPayload } = group as WithOptionalId<Group> & {
    memberIds?: string[];
    managerIds?: string[];
    trainerIds?: string[];
  };

  const createResponse = await getDataWithRetry<Group | null>(
    `${apiBaseUrl}groups`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(groupPayload),
    },
    isValidGroupResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }

  const associationCommands: Array<Promise<FetchResult<null>>> = [
    mutateGroupAssociation(
      createResponse.data.id,
      'trainers',
      'associate',
      safeTrainerIds,
    ),
    mutateGroupAssociation(
      createResponse.data.id,
      'members',
      'associate',
      safeMemberIds,
    ),
  ];
  for (const command of associationCommands) {
    const result = await command;
    if (!result.success) {
      return result;
    }
  }

  return fetchGroupById(createResponse.data.id);
};

export const updateGroupData = async (
  oldGroupData: Group,
  newGroupData: Group,
): Promise<FetchResult<Group | null>> => {
  const groupId = newGroupData.id;
  if (!groupId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Group id is required for update'),
    };
  }

  const {
    memberIds: _m,
    managerIds: _mg,
    trainerIds: _t,
    createdDate: _cd,
    updatedDate: _ud,
    programName: _pn,
    memberCount: _mc,
    trainerName: _tn,
    ...groupPayload
  } = newGroupData as Group & {
    memberIds?: string[];
    managerIds?: string[];
    trainerIds?: string[];
    createdDate?: string;
    updatedDate?: string;
    programName?: string;
    memberCount?: number;
    trainerName?: string;
  };

  const updateResponse = await getDataWithRetry<Group | null>(
    `${apiBaseUrl}groups`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(groupPayload),
    },
    isValidGroupResponse,
  );
  if (!updateResponse.success) {
    return updateResponse;
  }

  const safeOldTrainerIds = oldGroupData.trainerIds ?? [];
  const safeNewTrainerIds = newGroupData.trainerIds ?? [];
  const safeOldMemberIds = oldGroupData.memberIds ?? [];
  const safeNewMemberIds = newGroupData.memberIds ?? [];
  const safeOldManagerIds = oldGroupData.managerIds ?? [];
  const safeNewManagerIds = newGroupData.managerIds ?? [];

  const trainerIdsToUnassociate = _.difference(
    safeOldTrainerIds,
    safeNewTrainerIds,
  );
  const trainerIdsToAssociate = _.difference(
    safeNewTrainerIds,
    safeOldTrainerIds,
  );
  const memberIdsToUnassociate = _.difference(
    safeOldMemberIds,
    safeNewMemberIds,
  );
  const memberIdsToAssociate = _.difference(safeNewMemberIds, safeOldMemberIds);
  const managerIdsToUnassociate = _.difference(
    safeOldManagerIds,
    safeNewManagerIds,
  );
  const managerIdsToAssociate = _.difference(
    safeNewManagerIds,
    safeOldManagerIds,
  );

  const associationWorkflows: Array<Promise<FetchResult<null>>> = [
    mutateGroupAssociation(
      groupId,
      'trainers',
      'unassociate',
      trainerIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'trainers',
      'associate',
      trainerIdsToAssociate,
    ),
    mutateGroupAssociation(
      groupId,
      'members',
      'unassociate',
      memberIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'members',
      'associate',
      memberIdsToAssociate,
    ),
    mutateGroupAssociation(
      groupId,
      'managers',
      'unassociate',
      managerIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'managers',
      'associate',
      managerIdsToAssociate,
    ),
  ];

  for (const workflow of associationWorkflows) {
    const result = await workflow;
    if (!result.success) {
      return result;
    }
  }

  return fetchGroupById(groupId);
};

export const deleteGroupById = async (
  groupId: string,
): Promise<FetchResult<null>> => {
  if (!groupId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Group not found'),
    };
  }
  const deleteResult = await sendWithoutBody(`${apiBaseUrl}groups/${groupId}`, {
    method: 'DELETE',
  });
  return deleteResult;
};

export const fetchGroupsByIds = async (
  groupIds: string[],
): Promise<FetchResult<Group[]>> => {
  if (!groupIds || groupIds.length === 0) {
    return createSuccessResult<Group[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Group>>(
    `${apiBaseUrl}groups/getByIds?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(groupIds),
    },
    isValidGroupsResponse,
  );
  return mapPagedResult(response);
};

export const fetchGroupsPageByIds = (
  groupIds: string[],
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<Group>>> => {
  if (!groupIds.length) {
    return Promise.resolve(
      createSuccessResult(createEmptyPagedResponse<Group>(params)),
    );
  }
  return getDataWithRetry<PagedResponse<Group>>(
    `${apiBaseUrl}groups/getByIds?${buildPaginationQuery(params)}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(groupIds),
    },
    isValidGroupsResponse,
  );
};

export const fetchGroupsByGymId = (
  gymId: string,
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<Group>>> => {
  if (!gymId) {
    return Promise.resolve(
      createSuccessResult(createEmptyPagedResponse<Group>(params)),
    );
  }
  return getDataWithRetry<PagedResponse<Group>>(
    `${apiBaseUrl}groups/gym/${gymId}?${buildPaginationQuery(params)}`,
    {},
    isValidGroupsResponse,
  );
};

export const getGymGroups = async (
  gymId: string,
): Promise<FetchResult<Group[]>> => {
  if (!gymId) {
    return createSuccessResult<Group[]>([]);
  }

  const firstResponse = await getDataWithRetry<PagedResponse<Group>>(
    `${apiBaseUrl}groups/gym/${gymId}?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {},
    isValidGroupsResponse,
  );
  if (!firstResponse.success) {
    return firstResponse;
  }

  const firstPageGroups = firstResponse.data.content ?? [];
  const { totalPages } = getPagedMeta(firstResponse.data);
  if (totalPages <= 1) {
    return {
      ...firstResponse,
      data: firstPageGroups,
    };
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getDataWithRetry<PagedResponse<Group>>(
        `${apiBaseUrl}groups/gym/${gymId}?page=${index + 1}&size=${MAX_PAGE_SIZE}&sort=name`,
        {},
        isValidGroupsResponse,
      ),
    ),
  );
  for (const response of remainingResponses) {
    if (!response.success) {
      return response;
    }
  }

  return {
    ...firstResponse,
    data: [
      ...firstPageGroups,
      ...remainingResponses.flatMap((response) =>
        response.success ? response.data.content ?? [] : [],
      ),
    ],
  };
};
